import { httpsCallable } from 'firebase/functions'
import Swal from 'sweetalert2/dist/sweetalert2.js'

import functions from '@/config/firebase/functions'
import BusinessError from '@/commands/BusinessError'
import alertsBus from '@/services/alerts/alertsBus'
import { beginRequest, endRequest } from '@/services/requestState'

/**
 * Point d'entrée unique des mutations.
 *
 * Le client n'écrit jamais dans Firestore : il exprime une intention métier, la Cloud
 * Function valide et écrit, et les listeners déjà en place rafraîchissent l'interface.
 *
 * Toute mutation réussie est signalée sur `alertsBus` : comme c'est ici que passe la
 * totalité des écritures, le centre d'alertes reste à jour quelle que soit l'action, sans
 * que chaque écran ait à le prévenir.
 *
 * Pour la même raison, l'appel est compté dans `requestState` : l'indicateur global sait
 * ainsi qu'une opération est en cours, sans qu'aucun écran ait à gérer son propre voyant.
 * Le jeton est rendu dans `finally` — un appel en échec doit éteindre le voyant autant
 * qu'un appel réussi, sinon l'application resterait bloquée sur une attente fantôme.
 *
 * @param {string} name nom de la Cloud Function
 * @param {object} payload informations strictement nécessaires à l'intention
 * @returns {Promise<object>} charge utile renvoyée par la fonction
 * @throws {BusinessError} en cas de refus métier
 */
/**
 * Durée maximale d'exécution des fonctions longues, en secondes, telle que déclarée côté
 * serveur (`timeoutSeconds`).
 *
 * Le SDK abandonne un appel au bout de 70 secondes par défaut, quelle que soit la durée
 * accordée à la fonction : le navigateur affichait « deadline-exceeded » alors que le
 * serveur, lui, poursuivait son travail — et l'écran, croyant à un échec, n'enchaînait
 * pas la suite. Le client attend donc désormais aussi longtemps que le serveur peut
 * travailler ; c'est le serveur qui fixe la limite, et qui répond par une erreur s'il la
 * dépasse.
 */
const SERVER_TIMEOUTS = {
  runPeriodSimulation: 3600,
  migrateLegacyWorld: 540,
  clearPeriodResults: 540,
  requestNewContractsForAllTeams: 540,
  requestNewStaffForAllTeams: 540,
  requestNewContractsForOffer: 540,
  exportWorldData: 540,
  importWorldData: 540,
  resetWorldGameData: 540,
  generateStrategyContractPool: 300,
  generateStrategyStaffPool: 300,
  initializeTeamStates: 300,
  copyStrategyToAllTeams: 300,
  applyStrategySupplierToAllTeams: 300,
  ensurePeriodBootstrap: 120,
  updateStrategyStaffSalary: 120,
  resetStrategyStaffSalary: 120,
  updateStrategyStaffMoneyBonus: 120,
  createTemplate: 120,
  updateTemplate: 120,
  deleteTemplate: 120,
}

/** Les fonctions du référentiel (create…, update…, delete…) disposent de 300 secondes. */
const CATALOGUE_TIMEOUT = 300
const DEFAULT_TIMEOUT = 60

/** Délai d'attente côté client : la limite du serveur, plus une marge de transport. */
function clientTimeout(name) {
  const seconds = SERVER_TIMEOUTS[name]
    || (/^(create|update|delete)[A-Z]/.test(name) ? CATALOGUE_TIMEOUT : DEFAULT_TIMEOUT)
  return (seconds + 15) * 1000
}

export async function callFunction(name, payload = {}) {
  const tracked = beginRequest(name)

  try {
    const callable = httpsCallable(functions, name, { timeout: clientTimeout(name) })
    const response = await callable(payload)
    const data = response && response.data ? response.data : {}

    if (data.success === false) {
      throw new BusinessError(data.code, data.message, data.details)
    }

    alertsBus.notify(name)
    return data
  } catch (error) {
    if (error instanceof BusinessError) throw error
    throw BusinessError.fromCallableError(error)
  } finally {
    endRequest(tracked)
  }
}

/**
 * Variante tolérante : exécute l'action et affiche l'erreur métier à l'utilisateur.
 * Retourne `null` si l'action a été refusée, la charge utile sinon.
 */
export async function callFunctionWithFeedback(name, payload = {}, { successMessage = null } = {}) {
  try {
    const data = await callFunction(name, payload)
    if (successMessage) {
      Swal.fire({ icon: 'success', title: successMessage, showConfirmButton: false, timer: 1500 })
    }
    return data
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Action impossible', text: error.message })
    return null
  }
}

export default callFunction
